import { StandardEvents } from '@shopify/events';

/**
 * Shows a bottom-fixed toast confirming an item was added to the cart,
 * with its image, title, and price, plus a "View Cart" action. Listens for
 * the theme's native cartLinesUpdate event so it works regardless of which
 * component triggered the add (product form, quick add, sticky bar, bundle
 * builder), with no changes needed to those components.
 */
class CartAddedToast extends HTMLElement {
  connectedCallback() {
    this.hideTimeout = null;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);

    const closeBtn = this.querySelector('[data-role="close"]');
    closeBtn?.addEventListener('click', () => this.#hide());

    const viewCartBtn = this.querySelector('[data-role="view-cart"]');
    viewCartBtn?.addEventListener('click', () => {
      const cartDrawer = document.querySelector('#cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        window.location.href = window.Theme?.routes?.cart_url || '/cart';
      }
      this.#hide();
    });
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const detail = event.detail;
    if (!detail || detail.action !== 'add' || !event.promise) return;

    event.promise
      .then(({ detail: resolvedDetail }) => {
        if (resolvedDetail?.didError) return;

        const items = resolvedDetail?.items;
        if (!Array.isArray(items) || items.length === 0) return;

        // Show the most recently added line item.
        const lastItem = items[items.length - 1];
        this.#show(lastItem);
      })
      .catch(() => {});
  };

  /**
   * @param {Record<string, any>} item
   */
  #show(item) {
    const image = this.querySelector('[data-role="image"]');
    const title = this.querySelector('[data-role="title"]');
    const price = this.querySelector('[data-role="price"]');

    if (image) {
      const src = item.image || item.featured_image?.url || item.product?.featured_image;
      if (src) {
        image.src = typeof src === 'string' ? src : src.url || '';
        image.alt = item.title || item.product_title || '';
      }
    }
    if (title) title.textContent = item.title || item.product_title || 'Item';
    if (price && typeof item.final_price === 'number') {
      price.textContent = this.#formatMoney(item.final_price);
    } else if (price && typeof item.price === 'number') {
      price.textContent = this.#formatMoney(item.price);
    }

    this.hidden = false;
    // Force reflow so the transition runs on every show, even repeated adds.
    void this.offsetHeight;
    this.dataset.visible = 'true';

    if (this.hideTimeout) clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => this.#hide(), 4500);
  }

  #hide() {
    this.dataset.visible = 'false';
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
    } catch (e) {
      // fall through to default formatting
    }
    return `₹${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('cart-added-toast')) {
  customElements.define('cart-added-toast', CartAddedToast);
}
